import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, CheckCircle, Cube, Robot } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HomePage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    size: 'none',
    speed: 'none',
    type: 'none',
    capacity: 'none'
  });

  const memorySizes = ['4GB', '8GB', '16GB', '32GB', '64GB', '128GB', '256GB', '512GB'];
  const ddr4Speeds = ['2666', '2933', '3200'];
  const ddr5Speeds = ['4800', '5600', '6400', '7200', '8800'];
  const memoryTypes = ['DDR4', 'DDR5'];
  const formFactors = ['RDIMM', 'LRDIMM', '3DS LRDIMM', 'UDIMM', 'SODIMM'];

  const handleBrowseInventory = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'none') params.append(key, value);
    });
    navigate(`/inventory?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_rfq-memory-direct/artifacts/4844jh0k_IMG_1773.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-7xl font-['JetBrains_Mono'] font-bold mb-6 tracking-tight">
            <span className="text-[#007AFF]">Server Memory RFQ</span> <span className="text-white">Network</span>
          </h1>
          <p className="text-xl lg:text-2xl text-[#94A3B8] mb-4 max-w-3xl mx-auto">
            M2M/MCP x402 Autonomous AI Agent Agentic Procurement
          </p>
          <p className="text-lg text-[#94A3B8] mb-8 max-w-3xl mx-auto">
            B2B Marketplace for Server Memory • New & Refurbished • Factory Sealed • 1 Year Warranty
          </p>
          <div className="flex flex-col gap-4 items-center">
            <div className="flex flex-wrap gap-4 justify-center">
              <button 
                data-testid="request-quote-btn"
                onClick={() => navigate('/rfq')} 
                className="aqua-button-primary text-white px-10 py-4 text-lg font-['JetBrains_Mono'] font-bold"
              >
                Request Quote
              </button>
              <button 
                data-testid="browse-inventory-btn"
                onClick={() => navigate('/inventory')} 
                className="aqua-button text-white px-10 py-4 text-lg font-['JetBrains_Mono'] font-semibold"
              >
                <MagnifyingGlass className="inline mr-2" size={24} />
                Browse Inventory
              </button>
            </div>
            <button 
              data-testid="ai-agent-connect-btn"
              onClick={() => navigate('/agents')} 
              className="mt-2 bg-gradient-to-r from-[#9945FF] to-[#14F195] text-white px-10 py-4 text-lg font-['JetBrains_Mono'] font-bold rounded-lg shadow-lg hover:shadow-[#9945FF]/30 transition-all flex items-center gap-2"
            >
              <Robot size={24} weight="fill" />
              AI Agent Web3 Connect
            </button>
          </div>
        </div>
      </section>

      {/* Memory Configurator Section */}
      <section id="memory-configurator" className="py-20 px-6" data-testid="memory-configurator">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Cube className="mx-auto mb-4 text-[#007AFF]" size={48} />
            <h2 className="text-4xl font-['JetBrains_Mono'] font-bold mb-4">
              Memory Configurator
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Drill down by specifications to find exactly what you need
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Memory Size */}
              <div>
                <label className="block text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
                  Memory Size
                </label>
                <Select value={filters.size || "none"} onValueChange={(value) => setFilters({...filters, size: value === "none" ? "" : value})}>
                  <SelectTrigger data-testid="size-select" className="bg-transparent border-[#334155] focus:border-[#007AFF]">
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select size</SelectItem>
                    {memorySizes.map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Memory Type */}
              <div>
                <label className="block text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
                  Memory Type
                </label>
                <Select value={filters.type || "none"} onValueChange={(value) => setFilters({...filters, type: value === "none" ? "" : value})}>
                  <SelectTrigger data-testid="type-select" className="bg-transparent border-[#334155] focus:border-[#007AFF]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select type</SelectItem>
                    {memoryTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Speed */}
              <div>
                <label className="block text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
                  Speed (MHz)
                </label>
                <Select value={filters.speed || "none"} onValueChange={(value) => setFilters({...filters, speed: value === "none" ? "" : value})}>
                  <SelectTrigger data-testid="speed-select" className="bg-transparent border-[#334155] focus:border-[#007AFF]">
                    <SelectValue placeholder="Select speed" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select speed</SelectItem>
                    {filters.type === 'DDR4' && ddr4Speeds.map(speed => (
                      <SelectItem key={speed} value={speed}>{speed}</SelectItem>
                    ))}
                    {filters.type === 'DDR5' && ddr5Speeds.map(speed => (
                      <SelectItem key={speed} value={speed}>{speed}</SelectItem>
                    ))}
                    {(!filters.type || filters.type === "none") && [...ddr4Speeds, ...ddr5Speeds].map(speed => (
                      <SelectItem key={speed} value={speed}>{speed}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Form Factor */}
              <div>
                <label className="block text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
                  Form Factor
                </label>
                <Select value={filters.capacity || "none"} onValueChange={(value) => setFilters({...filters, capacity: value === "none" ? "" : value})}>
                  <SelectTrigger data-testid="form-factor-select" className="bg-transparent border-[#334155] focus:border-[#007AFF]">
                    <SelectValue placeholder="Select form factor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select form factor</SelectItem>
                    {formFactors.map(factor => (
                      <SelectItem key={factor} value={factor}>{factor}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button 
                data-testid="find-memory-btn"
                onClick={handleBrowseInventory}
                className="aqua-button-primary text-white px-12 py-4 text-lg font-['JetBrains_Mono'] font-bold"
              >
                <MagnifyingGlass className="inline mr-2" size={20} />
                Find Memory
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who Uses It Section */}
      <section className="py-20 px-6 bg-[#11141D]/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-['JetBrains_Mono'] font-bold text-center mb-12">
            Who Uses <span className="text-[#007AFF]">Server Memory RFQ</span>?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'End Users', desc: 'Companies needing server memory upgrades' },
              { title: 'System Integrators', desc: 'Building custom server solutions' },
              { title: 'IT Resellers / VARs', desc: 'Sourcing parts for clients' },
              { title: 'Data Center Operators', desc: 'Managing large-scale infrastructure' },
              { title: 'Hardware Brokers', desc: 'Trading server components' },
              { title: 'System Builders', desc: 'Assembling server systems' }
            ].map((item, idx) => (
              <div key={idx} className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 hover:-translate-y-1 transition-transform">
                <h3 className="text-xl font-['JetBrains_Mono'] font-bold text-[#007AFF] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#94A3B8]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-6" data-testid="why-choose-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-10">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-['JetBrains_Mono'] font-bold mb-2 text-cyan-400">
                  Why Choose Us
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-lg">
                <div className="flex items-center gap-4 p-4 bg-[#05050A]/50 rounded-lg border border-[#1E293B]">
                  <div className="w-12 h-12 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={28} className="text-[#10B981]" weight="fill" />
                  </div>
                  <span className="text-[#F8FAFC] font-['JetBrains_Mono']">1 Year Warranty</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-[#05050A]/50 rounded-lg border border-[#1E293B]">
                  <div className="w-12 h-12 bg-[#007AFF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={28} className="text-[#007AFF]" weight="fill" />
                  </div>
                  <span className="text-[#F8FAFC] font-['JetBrains_Mono']">Fully Tested in Our Servers</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-[#05050A]/50 rounded-lg border border-[#1E293B]">
                  <div className="w-12 h-12 bg-[#F59E0B]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={28} className="text-[#F59E0B]" weight="fill" />
                  </div>
                  <span className="text-[#F8FAFC] font-['JetBrains_Mono']">Same Day Shipping</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-[#05050A]/50 rounded-lg border border-[#1E293B]">
                  <div className="w-12 h-12 bg-cyan-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={28} className="text-cyan-400" weight="fill" />
                  </div>
                  <span className="text-[#F8FAFC] font-['JetBrains_Mono']">Technical Support Team</span>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-[#1E293B] text-center">
                <p className="text-sm text-cyan-400 uppercase tracking-wider font-['JetBrains_Mono'] leading-relaxed">
                  100% GUARANTEED COMPATIBLE<br />
                  QUICK ADVANCE REPLACEMENT<br />
                  EXPERT TECHNICAL SUPPORT
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
